import { ButtonHint, Colors, ModalDialog, Text } from '@amelio/ui-library';
import { useState } from 'react';

import { anonymousRespondentCopy, type RespondentStanding } from './anonymousRespondentCopy';
import { PrivacySentence } from './PrivacySentence';

import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';

interface AnonymousPrivacyDetailsProps {
    /** Completed responses the 360 needs before its answers become visible. */
    minimumResponses: number;
    respondentStanding?: RespondentStanding;
}

/**
 * Opens from the short reminder under a question of an anonymous 360: the promises of the privacy screen,
 * in its own words, as this respondent stands.
 */
export const AnonymousPrivacyDetails = ({
    minimumResponses,
    respondentStanding = {},
}: AnonymousPrivacyDetailsProps) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();
    const [isOpen, setIsOpen] = useState(false);

    const { hidden, revieweeSentence, conditions } = anonymousRespondentCopy(respondentStanding);
    const values = { managerLabel: getManagerLabel(), minimum: minimumResponses };

    return (
        <>
            <ButtonHint
                onClick={() => setIsOpen(true)}
                aria-haspopup={'dialog'}
                className={
                    'text-responsive-xs-sm min-h-6 px-1 py-0 font-normal text-white underline underline-offset-2 hover:bg-white/10 pointer-coarse:min-h-11'
                }
                dataTestId={'anonymous-privacy-more-info'}
            >
                {t('answer.confidentiality.moreInfo')}
            </ButtonHint>

            <ModalDialog
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                closeAriaLabel={t('common.close')}
                title={t('answer.confidentiality.detailsTitle')}
                size={'sm'}
                dataTestId={'anonymous-privacy-details'}
            >
                <ul className={'flex flex-col gap-4'}>
                    {[hidden, revieweeSentence, ...conditions].map((copyKey) => (
                        <li key={copyKey}>
                            <Text color={Colors.BASE}>
                                <PrivacySentence
                                    copyKey={copyKey}
                                    values={values}
                                />
                            </Text>
                        </li>
                    ))}
                </ul>
            </ModalDialog>
        </>
    );
};
